<template>
  <!-- OVERLAY -->
  <div
    class="modal-overlay"
    @click.self="fechar"
  >
    <!-- MODAL -->
    <div
      class="modal-container"
      :class="{ 'modo-rejeicao': modoRejeicao }"
    >
      <!-- HEADER -->
      <div class="modal-header">
        <span class="modal-header-titulo">NOTÍCIA ABERTA</span>
        <button
          class="btn-fechar"
          @click="fechar"
          aria-label="Fechar"
        >
          ✕
        </button>
      </div>

      <!-- LOADING -->
      <Carregamento
        v-if="loading"
        :mensagemCarregamento="'carregando notícia'"
      />

      <!-- CONTEÚDO -->
      <div
        v-else-if="noticia"
        class="modal-body"
        :class="{ 'body-frozen': modoRejeicao }"
      >
        <!-- TÍTULO + META -->
        <div class="noticia-meta-topo">
          <h1 class="noticia-titulo">{{ noticia.titulo?.toUpperCase() }}</h1>
          <p class="noticia-meta">
            Data de publicação: {{ formatarData(noticia.dataPublicacao) }}
          </p>
          <p
            class="noticia-meta"
            v-if="
              noticia.dataAtualizacao &&
              formatarData(noticia.dataAtualizacao) !==
                formatarData(noticia.dataPublicacao)
            "
          >
            Data de atualização: {{ formatarData(noticia.dataAtualizacao) }}
          </p>
          <p class="noticia-meta">Autor: {{ noticia.autorPublicacaoNome }}</p>
        </div>

        <!-- TAGS -->
        <div
          class="noticia-tags"
          v-if="noticia.palavrasChave?.length"
        >
          <span
            class="tag"
            v-for="(palavra, index) in noticia.palavrasChave"
            :key="index"
          >
            {{ palavra }}
          </span>
        </div>

        <!-- LAYOUT DUAS COLUNAS: TEXTO + IMAGEM -->
        <div class="noticia-conteudo-grid">
          <!-- COLUNA ESQUERDA: TEXTO -->
          <div class="noticia-texto-col">
            <div
              class="noticia-texto"
              v-html="noticia.texto || noticia.descricao"
            ></div>
          </div>

          <!-- COLUNA DIREITA: IMAGEM -->
          <div
            class="noticia-imagem-col"
            v-if="noticia.imagemUrl"
          >
            <img
              :src="noticia.imagemUrl"
              :alt="noticia.descricaoImagem"
              class="noticia-imagem"
            />
            <div
              class="noticia-info-extra"
              v-if="noticia.fonte"
            >
              <p>
                <strong>Fonte:</strong>
                {{
                  noticia.fonte?.length > 0
                    ? noticia.fonte
                    : "Fonte não especificada"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- NÃO ENCONTRADA -->
      <div
        v-else
        class="modal-vazio"
      >
        <p>Notícia não encontrada.</p>
      </div>

      <!-- SEÇÃO DE REJEIÇÃO (aparece sobre o footer quando rejeitar é clicado) -->
      <div
        v-if="modoRejeicao"
        class="rejeicao-section"
      >
        <div class="rejeicao-divider-label">Justificar Rejeição</div>
        <div class="rejeicao-body">
          <label class="rejeicao-label">*Diga o motivo da Rejeição</label>
          <textarea
            v-model="motivoRejeicao"
            class="rejeicao-textarea"
            placeholder=""
            rows="5"
          ></textarea>
          <div class="rejeicao-footer">
            <button
              class="btn-voltar"
              @click="cancelarRejeicao"
              :disabled="processando"
            >
              Voltar
            </button>
            <button
              class="btn-gravar"
              @click="confirmarRejeicao"
              :disabled="processando || !motivoRejeicao.trim()"
            >
              {{ processando ? "Gravando..." : "Gravar" }}
            </button>
          </div>
        </div>
      </div>

      <!-- RODAPÉ COM BOTÕES (visível apenas fora do modo rejeição) -->
      <div
        class="modal-footer"
        v-if="noticia && mostrarBotoes && !modoRejeicao"
      >
        <button
          class="btn-rejeitar"
          @click="iniciarRejeicao"
          :disabled="processando"
        >
          Rejeitar
        </button>
        <button
          class="btn-aceitar"
          @click="aceitar"
          :disabled="processando"
        >
          {{ processando ? "Processando..." : "Aceitar" }}
        </button>
      </div>
    </div>
  </div>

  <Aviso
    v-if="avisoView"
    :texto="mensagemAviso"
    :sucesso="sucesso"
    @fecharModal="fecharModalETelaView()"
  />

  <ModalConfirmarSenha
    :mostrar="modalSenhaVisivel"
    :carregando="verificandoSenha"
    :erro="erroSenha"
    @cancelar="fecharModalSenha"
    @confirmar="onConfirmarSenha"
  />
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import noticiaService from "@/service/noticiaService";
import usuariosService from "@/service/usuariosService";
import Aviso from "@/components/common/aviso.vue";
import Carregamento from "@/components/common/Carregamento.vue";
import ModalConfirmarSenha from "@/components/usuarios/ModalConfirmarSenha.vue";

const props = defineProps({
  noticiaId: { type: [String, Number], default: null },
  noticiaObjeto: { type: Object, default: null },
  mostrarBotoes: { type: Boolean, default: true },
});

const emit = defineEmits(["fechar", "aceita", "rejeitada"]);

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const processando = ref(false);
const noticia = ref(null);
const avisoView = ref(false);
const sucesso = ref(false);
const mensagemAviso = ref("");

// Controle do modo rejeição
const modoRejeicao = ref(false);
const motivoRejeicao = ref("");

// Controle do modal de senha
const modalSenhaVisivel = ref(false);
const verificandoSenha = ref(false);
const erroSenha = ref("");
const acaoPendente = ref(""); // "aceitar" ou "rejeitar"

function fecharModalSenha() {
  modalSenhaVisivel.value = false;
  erroSenha.value = "";
}

const resolvedId = computed(() => {
  return (
    props.noticiaId || route.params.noticiaId || route.query.noticiaId || null
  );
});

function formatarData(data) {
  if (!data) return "DD/MM/AAAA";
  try {
    const d = new Date(data);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return data;
  }
}

function fechar() {
  emit("fechar");
  if (route.name === "noticia-prex-view") {
    router.back();
  }
}

async function fecharModalETelaView() {
  if (sucesso.value == false) {
    avisoView.value = false;
    return;
  }
  avisoView.value = false;
  await new Promise((resolve) => setTimeout(resolve, 300));
  fechar();
}

// Inicia o modo de rejeição (mostra o campo de motivo)
function iniciarRejeicao() {
  motivoRejeicao.value = "";
  modoRejeicao.value = true;
}

// Cancela e volta para o estado normal
function cancelarRejeicao() {
  modoRejeicao.value = false;
  motivoRejeicao.value = "";
}

// Confirma a rejeição com motivo
function confirmarRejeicao() {
  if (!noticia.value?.id || !motivoRejeicao.value.trim()) return;
  acaoPendente.value = "rejeitar";
  modalSenhaVisivel.value = true;
}

// Aceita a notícia
function aceitar() {
  if (!noticia.value?.id) return;
  acaoPendente.value = "aceitar";
  modalSenhaVisivel.value = true;
}

async function onConfirmarSenha(senha) {
  verificandoSenha.value = true;
  erroSenha.value = "";
  try {
    const valida = await usuariosService.verificarSenha(senha);
    if (!valida) {
      erroSenha.value = "Senha incorreta. Tente novamente.";
      return;
    }
    fecharModalSenha();
    if (acaoPendente.value === "aceitar") {
      await executarAceitacao();
    } else if (acaoPendente.value === "rejeitar") {
      await executarRejeicao();
    }
  } catch (error) {
    erroSenha.value = "Erro ao validar senha.";
  } finally {
    verificandoSenha.value = false;
  }
}

async function executarAceitacao() {
  processando.value = true;
  try {
    await noticiaService.autorizarNoticia(noticia.value.id, true);
    emit("aceita", noticia.value);
    sucesso.value = true;
    mensagemAviso.value = "Notícia autorizada com sucesso!";
    avisoView.value = true;
  } catch (error) {
    sucesso.value = false;
    mensagemAviso.value = "Erro ao aceitar notícia!";
    avisoView.value = true;
    console.error("Erro ao aceitar notícia:", error);
  } finally {
    processando.value = false;
  }
}

async function executarRejeicao() {
  processando.value = true;
  try {
    await noticiaService.autorizarNoticia(
      noticia.value.id,
      false,
      motivoRejeicao.value.trim()
    );

    emit("rejeitada", noticia.value);
    sucesso.value = true;
    mensagemAviso.value = "Notícia rejeitada com sucesso!";
    avisoView.value = true;
    modoRejeicao.value = false;
  } catch (error) {
    console.error("Erro ao rejeitar notícia:", error);
    sucesso.value = false;
    mensagemAviso.value = "Erro ao rejeitar notícia!";
    avisoView.value = true;
  } finally {
    processando.value = false;
  }
}

async function carregarNoticia() {
  loading.value = true;
  try {
    const response = await noticiaService.getNoticiaById(resolvedId.value);
    noticia.value = response;
  } catch (error) {
    console.error("Erro ao carregar notícia:", error);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await carregarNoticia();
});
</script>

<style scoped>
/* OVERLAY */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 5, 30, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(2px);
}

/* CONTAINER DO MODAL */
.modal-container {
  background: #ffffff;
  border-radius: 6px;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

/* HEADER */
.modal-header {
  background: #1a0f3e;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}

.modal-header-titulo {
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.btn-fechar {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
  transition: background-color 0.2s ease;
}

.btn-fechar:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* BODY */
.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: max-height 0.3s ease;
}

/* Quando em modo rejeição, o body fica com altura reduzida (efeito "congelado") */
.modal-body.body-frozen {
  flex: 0 0 auto;
  max-height: 260px;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  opacity: 0.85;
}

/* META TOPO */
.noticia-meta-topo {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.noticia-titulo {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.3;
  margin: 0 0 6px 0;
  text-transform: uppercase;
}

.noticia-meta {
  font-size: 0.8rem;
  color: #6c757d;
  margin: 0;
}

/* TAGS */
.noticia-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  padding: 4px 12px;
  border: 1px solid #aaa;
  border-radius: 4px;
  font-size: 0.78rem;
  color: #444;
  background: transparent;
}

/* GRID DUAS COLUNAS */
.noticia-conteudo-grid {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 20px;
  align-items: start;
}

/* TEXTO */
.noticia-texto-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.noticia-texto {
  font-size: 0.83rem;
  color: #2c2c2c;
  line-height: 1.65;
}

.noticia-texto :deep(p) {
  margin-bottom: 10px;
}

.noticia-texto :deep(strong) {
  font-weight: 700;
  color: #1a1a2e;
}

/* IMAGEM */
.noticia-imagem-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.noticia-imagem {
  width: 100%;
  border-radius: 6px;
  object-fit: cover;
  max-height: 220px;
}

.noticia-info-extra {
  font-size: 0.78rem;
  color: #555;
  line-height: 1.5;
}

.noticia-info-extra p {
  margin: 0 0 4px 0;
}

/* FOOTER */
.modal-footer {
  padding: 14px 24px;
  display: flex;
  justify-content: center;
  gap: 16px;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
  background: #fff;
}

.btn-rejeitar,
.btn-aceitar {
  padding: 9px 36px;
  border-radius: 5px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition:
    opacity 0.2s ease,
    transform 0.1s ease,
    background-color 0.2s ease;
  min-width: 110px;
}

.btn-rejeitar {
  background: #ef4444;
  color: white;
}

.btn-rejeitar:hover:not(:disabled) {
  background: #dc2626;
}

.btn-aceitar {
  background: #22c55e;
  color: white;
}

.btn-aceitar:hover:not(:disabled) {
  background: #16a34a;
}

.btn-rejeitar:disabled,
.btn-aceitar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-rejeitar:active:not(:disabled),
.btn-aceitar:active:not(:disabled) {
  transform: scale(0.97);
}

/* ================================
   SEÇÃO DE REJEIÇÃO
   ================================ */
.rejeicao-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

/* Faixa com label "Justificar Rejeição" */
.rejeicao-divider-label {
  background: #f0edf8;
  color: #362065;
  font-size: 0.82rem;
  font-weight: 700;
  text-align: center;
  padding: 8px 24px;
  letter-spacing: 0.04em;
  border-top: 1px solid #d6cef0;
  border-bottom: 1px solid #d6cef0;
}

/* Corpo da rejeição */
.rejeicao-body {
  padding: 20px 24px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rejeicao-label {
  font-size: 0.83rem;
  font-weight: 600;
  color: #1a1a2e;
}

.rejeicao-textarea {
  width: 100%;
  min-height: 120px;
  border: 1.5px solid #c5bce0;
  border-radius: 5px;
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #2c2c2c;
  resize: vertical;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.rejeicao-textarea:focus {
  border-color: #362065;
  box-shadow: 0 0 0 3px rgba(54, 32, 101, 0.08);
}

/* Botões da rejeição */
.rejeicao-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 4px;
}

.btn-voltar,
.btn-gravar {
  padding: 9px 36px;
  border-radius: 5px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition:
    opacity 0.2s ease,
    transform 0.1s ease;
  min-width: 110px;
}

.btn-voltar {
  background: #e0e0e0;
  color: #333;
}

.btn-voltar:hover:not(:disabled) {
  background: #cacaca;
}

.btn-gravar {
  background: #362065;
  color: white;
}

.btn-gravar:hover:not(:disabled) {
  background: #48298a;
}

.btn-voltar:disabled,
.btn-gravar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-voltar:active:not(:disabled),
.btn-gravar:active:not(:disabled) {
  transform: scale(0.97);
}

/* LOADING / VAZIO */
.modal-loading,
.modal-vazio {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #6c757d;
  font-size: 0.95rem;
}

/* RESPONSIVO */
@media (max-width: 600px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal-container {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 12px 12px 0 0;
  }

  .noticia-conteudo-grid {
    grid-template-columns: 1fr;
  }

  .noticia-imagem-col {
    order: -1;
  }

  .noticia-titulo {
    font-size: 1rem;
  }

  .modal-footer,
  .rejeicao-footer {
    padding: 12px 16px;
  }

  .btn-rejeitar,
  .btn-aceitar,
  .btn-voltar,
  .btn-gravar {
    flex: 1;
  }
}
</style>
